import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import {
    CaseAutomaticNotificationTriggerCreateRequest,
    CaseAutomaticNotificationTriggerPatchInBatchRequest,
    CaseAutomaticNotificationTriggerResponse
} from '@/modules/configurationModule/detail/automation/notifications/automation-notifications-case/services/configuration-case-automatic-notification-trigger/configuration-case-automatic-notification-trigger.dto'
import { configurationCaseAutomaticNotificationTriggerService } from '@/modules/configurationModule/detail/automation/notifications/automation-notifications-case/services/configuration-case-automatic-notification-trigger/configuration-case-automatic-notification-trigger.service'
import {
    CaseAutomaticNotificationTriggerGridState,
    initialCaseAutomaticNotificationTriggerGridState
} from '@/modules/configurationModule/detail/automation/notifications/automation-notifications-case/components/case-automatic-notification-trigger-grid/case-automatic-notification-trigger-grid.model'
import CaseAutomaticNotificationTriggerModal
    from '@/modules/configurationModule/detail/automation/notifications/automation-notifications-case/components/case-automatic-notification-trigger-modal/case-automatic-notification-trigger-modal.vue'

@Component({
    components: {
        CaseAutomaticNotificationTriggerModal,
        ConfigurationSectionHeader,
        VtGrid
    }
})
export default class CaseAutomaticNotificationTriggerGrid extends BaseCrudServerSideComponent<CaseAutomaticNotificationTriggerResponse> implements BaseCrud {
    @Ref() readonly caseAutomaticNotificationTriggerModalWrapper!: any

    state: CaseAutomaticNotificationTriggerGridState
    constructor() {
        super()
        this.state = initialCaseAutomaticNotificationTriggerGridState()
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }
    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    /// SERVICES CALLS ///

    private async _createCaseAutomaticNotificationTrigger(createRequest: CaseAutomaticNotificationTriggerCreateRequest): Promise<CaseAutomaticNotificationTriggerResponse | null> {
        const response =
            await this.$_toCallUnique(
                configurationCaseAutomaticNotificationTriggerService.create(
                    createRequest,
                    this.state.laboratoryId))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
        }
        return response
    }

    private async _patchCaseAutomaticNotificationTriggers(batchRequests: CaseAutomaticNotificationTriggerPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response =
                await this.$_toCallUnique(
                    configurationCaseAutomaticNotificationTriggerService.patch(
                        batchRequests,
                        this.state.laboratoryId))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteCaseAutomaticNotificationTriggers(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response =
                await this.$_toCallUnique(
                    configurationCaseAutomaticNotificationTriggerService.delete(
                        requestDelete,
                        this.state.laboratoryId))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: CaseAutomaticNotificationTriggerCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createCaseAutomaticNotificationTrigger(createRequest)) {
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: CaseAutomaticNotificationTriggerPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchCaseAutomaticNotificationTriggers([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }
    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteCaseAutomaticNotificationTriggers(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onAdd(): void {
        this.showModal(
            this.$tc('configuration.actions.add'),
            null,
            this.caseAutomaticNotificationTriggerModalWrapper)
    }

    onUpdate(caseAutomaticNotificationTrigger: CaseAutomaticNotificationTriggerResponse = this.state.selectedRows[0]): void {
        if (caseAutomaticNotificationTrigger) {
            this.showModal(
                this.$tc('configuration.actions.update'),
                caseAutomaticNotificationTrigger,
                this.caseAutomaticNotificationTriggerModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(selectedRow => selectedRow.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (params?.node) {
            const selected: CaseAutomaticNotificationTriggerResponse = params.node.data

            return [
                {
                    name: this.$tc('configuration.actions.update'),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                    action: () => this.onUpdate(selected)
                },
                {
                    name: this.$tc('configuration.actions.delete'),
                    cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                    action: this.onDelete
                }
            ]
        }
        return []
    }
}
